import { object, string, boolean, array, mixed, lazy } from "yup";

export default intl => {
  const requiredText = intl.formatMessage({ id: "app.form.required" });

  return object().shape({
    codename: string().required(requiredText),
    description: string().required(requiredText),
    file: mixed().when("test", {
      // Test templates bypass form-field validation — the EVU tester handles content feedback.
      is: true,
      then: mixed().nullable(),
      otherwise: lazy(value => {
      // set_dns_names is only present when the visual form has been opened and populated.
      // Raw EVU JSON files never contain this UI-only field, so it's the correct
      // discriminator between form mode and file-upload mode.
      const isFormOpened = value && ('set_dns_names' in value);
      const isFileUploaded = value && ('stream_sources' in value || 'name' in value) && !isFormOpened;

      if (!isFormOpened && !isFileUploaded) {
        return mixed().test(
          "file-required",
          intl.formatMessage(
            { id: "roomTemplate.file.uploadOrOpenForm" },
            { defaultMessage: "Please upload a JSON file or open the form to configure the template" },
          ),
          () => false,
        );
      }

      if (isFileUploaded) {
        // Raw JSON uploaded — content validation is handled by the EVU tester service
        return mixed().nullable();
      }

      // If file has data, validate its structure
      return object()
        .shape({
          name: string().required(requiredText),
          set_dns_names: array().of(string()),
          stream_sources: array()
            .of(
              object().shape({
                label: string().required(
                  intl.formatMessage(
                    { id: "roomTemplate.streamSources.labelRequired" },
                    { defaultMessage: "Stream source label is required" },
                  ),
                ),
                // "type" is optional — absent means "not set" and is left untouched.
                // But if the template explicitly has the field, it can't be empty. The form
                // has no way to delete the field outright, so the only fix offered is to
                // select "stream" (the EVU default) from the Type dropdown.
                type: string().test(
                  "type-not-empty",
                  intl.formatMessage(
                    { id: "roomTemplate.streamSources.typeEmpty" },
                    {
                      defaultMessage: 'The "Type" field cannot be empty. Set it to "stream", which is the default value.',
                    },
                  ),
                  value => value === undefined || value === null || value.trim() !== "",
                ),
                cam_id: string(),
                url: string().test(
                  "vptz-dewarp-id",
                  function(value) {
                    const source = this.parent;
                    // Only validate vptz sources
                    if (!source.label?.startsWith("vptz")) {
                      return true;
                    }
                    // Check if URL has dewarp_id parameter with a value
                    if (value) {
                      const urlParams = new URLSearchParams(value.split("?")[1] || "");
                      const dewarpId = urlParams.get("dewarp_id");
                      if (!dewarpId || dewarpId.trim() === "") {
                        return this.createError({
                          message: intl.formatMessage(
                            { id: "roomTemplate.vptz.dewarpIdRequired" },
                            { label: source.label, defaultMessage: `Virtual camera "${source.label}" requires a dewarp_id parameter` },
                          ),
                        });
                      }
                    }
                    return true;
                  },
                ),
                steerable: object().shape({
                  cfg: string().test(
                    "steerable-cfg-required",
                    intl.formatMessage(
                      { id: "roomTemplate.streamSources.steerable.cfgRequired" },
                      { defaultMessage: "Settings file is required when Camera Operator is enabled" },
                    ),
                    function(value) {
                      const steerable = this.parent;
                      // Only validate if steerable is actually enabled (has method property)
                      // Empty steerable objects should not trigger validation
                      if (!steerable || !steerable.method) {
                        return true;
                      }
                      // If steerable is enabled, cfg is required
                      return !!value && value.trim() !== "";
                    },
                  ),
                }).nullable(),
                exp_camera_cfg: string()
                  .test(
                    "exp_camera_cfg-conditional",
                    intl.formatMessage(
                      { id: "roomTemplate.streamSources.expCameraCfgRequired" },
                      { defaultMessage: "Exposure Config File is required when Auto Exposure is enabled" },
                    ),
                    function(value) {
                      const source = this.parent;
                      // Only required when user explicitly enables Auto Exposure
                      if (!source.useAutoExposure) {
                        return true;
                      }
                      return !!value && value.trim() !== "";
                    },
                  )
                  .test(
                    "exp_camera_cfg-format",
                    intl.formatMessage(
                      { id: "roomTemplate.streamSources.expCameraCfgInvalidUrl" },
                      { defaultMessage: "Exposure Config File must be a valid URL" },
                    ),
                    function(value) {
                      const source = this.parent;
                      if (!source.useAutoExposure) {
                        return true;
                      }
                      if (!value || value.trim() === "") {
                        return true;
                      }
                      try {
                        new URL(value);
                        return true;
                      } catch {
                        return false;
                      }
                    },
                  )
                  .test(
                    "exp_camera_cfg-json",
                    intl.formatMessage(
                      { id: "roomTemplate.streamSources.expCameraCfgMustBeJson" },
                      { defaultMessage: "Exposure Config File must be a JSON file (ending with .json)" },
                    ),
                    function(value) {
                      const source = this.parent;
                      if (!source.useAutoExposure) {
                        return true;
                      }
                      if (!value || value.trim() === "") {
                        return true;
                      }
                      try {
                        const url = new URL(value);
                        return url.pathname.toLowerCase().endsWith(".json");
                      } catch {
                        return true;
                      }
                    },
                  ),
                exp_camera_url: string()
                  .test(
                    "exp_camera_url-format",
                    intl.formatMessage(
                      { id: "roomTemplate.streamSources.expCameraUrlInvalid" },
                      { defaultMessage: "Exposure Camera URL must be a valid URL" },
                    ),
                    function(value) {
                      const source = this.parent;
                      if (!source.useAutoExposure) {
                        return true;
                      }
                      if (!value || value.trim() === "") {
                        return true;
                      }
                      try {
                        new URL(value);
                        return true;
                      } catch {
                        return false;
                      }
                    },
                  ),
              }),
            )
            .min(
              1,
              intl.formatMessage(
                { id: "roomTemplate.streamSources.minRequired" },
                { defaultMessage: "At least one stream source is required" },
              ),
            ),
          // Virtualcam validation only applies when the object exists (user enabled it)
          // lazy() prevents Yup 0.27 from coercing undefined → {} and running the
          // shape validation when VirtualCam is disabled (value is null/undefined).
          virtualcam: lazy(virtualcamValue =>
            virtualcamValue
              ? object().shape({
                  config_file: string().required(requiredText),
                  scheduler: array()
                    .of(
                      object().shape({
                        name: string().required(
                          intl.formatMessage(
                            { id: "roomTemplate.virtualcam.scheduler.nameRequired" },
                            { defaultMessage: "Scheduler entry name is required" },
                          ),
                        ),
                        time: string()
                          .required(
                            intl.formatMessage(
                              { id: "roomTemplate.virtualcam.scheduler.timeRequired" },
                              { defaultMessage: "Scheduler entry time is required" },
                            ),
                          )
                          .matches(
                            /^\d{6}Z$/,
                            intl.formatMessage(
                              { id: "roomTemplate.virtualcam.scheduler.timeFormat" },
                              { defaultMessage: "Time must be in format HHMMSSZ (e.g., 120000Z)" },
                            ),
                          ),
                      }),
                    )
                    .nullable(),
                  sky_dome: string().nullable(),
                })
              : mixed().nullable()
          ),
          mixer: object().shape({
            score: object().shape({
              titler_url: string().test(
                "titler_url-required-when-use-titler",
                intl.formatMessage(
                  { id: "roomTemplate.mixer.score.titlerUrlRequired" },
                  { defaultMessage: "Titler URL is required when Use Titler is enabled" },
                ),
                function(value) {
                  const useTitler = this.parent?.use_titler;
                  if (useTitler) {
                    return !!value && value.trim() !== "";
                  }
                  return true;
                },
              ),
              drawn: object().shape({
                cfg_src: string().test(
                  "cfg_src-required-when-auto",
                  intl.formatMessage(
                    { id: "roomTemplate.mixer.drawn.cfgSrcRequired" },
                    { defaultMessage: "Config URL is required when Auto is enabled" },
                  ),
                  function(value) {
                    const auto = this.parent?.auto;
                    // If auto is enabled, cfg_src is required
                    if (auto) {
                      return !!value && value.trim() !== "";
                    }
                    // If auto is not enabled, cfg_src is optional
                    return true;
                  },
                ),
              }),
            }),
          }),
        });
    }),
    }),
    test: boolean().nullable(),
  });
};
